import React, {
  createContext,
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { debounce, throttle } from "lodash";

import { useGlobalState } from "common/global-state";
import { useVisibilityChange } from "common/hooks";
import { envVars } from "common/env";
import { WEBSOCKET_CONNECT_DELAY, WEBSOCKET_PING_DELAY, WEBSOCKET_PING_TIMEOUT_DELAY, ALL_SITES_ID } from "common/consts"

import { ConfigHashChangeHandler, ContextDataHandler, ContextIdentifier, ContextSubscriptions, MetricsMessage, PongMessage, RequestMessage } from "./types";


interface IWebSocket {
  subscribeContext: (siteId: string, context: string, handler: ContextDataHandler) => void;
  unsubscribeContext: (siteId: string, context: string) => void;
  subscribeConfigHash: (siteId: string, handler: ConfigHashChangeHandler) => void;
  unsubscribeConfigHash: (siteId: string) => void;
}

export const contextId: ContextIdentifier = (siteId, context) => `${siteId}/${context}`;
export const contextIdSplit = (contextId: string): string[] => contextId.split("/");


const WebSocketContext = createContext<IWebSocket>({} as IWebSocket);


export const WebSocketProvider: FunctionComponent<{}> = ({ children }) => {
  const socketRef = useRef<WebSocket | null>(null);
  const dataRef = useRef<Record<string, any>>({});
  const { user, setConnected } = useGlobalState();
  const subscriptions = useRef<ContextSubscriptions>({});
  const configHashes = useRef<Record<string, string>>({});
  const configHashListeners = useRef<Record<string, ConfigHashChangeHandler>>({});
  const documentVisible = useVisibilityChange();
  const isFirstRender = useRef(true);

  const socketSend = useCallback((message_type: string, siteId?: string, context?: string): void => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      const message: RequestMessage = {
        type: message_type,
        timestamp: +new Date(),
        version: envVars.VERSION,
      }

      if (message.type !== "ping") console.log(`[WS] Send ${message.type} ${context}`);

      switch(message.type) {
        case "ping": socketRef.current?.send(JSON.stringify(message)); break;
        case "subscribe": socketRef.current?.send(JSON.stringify({...message, ...{ site_id: siteId, context: context }})); break;
        case "unsubscribe": socketRef.current?.send(JSON.stringify({...message, ...{ site_id: siteId, context: context }})); break;
      }
    }
  }, []);

  const socketDisconnect = useCallback(() => {
    socketRef.current?.close();
    socketRef.current = null;
  }, []);

  const timeoutPing = useMemo(() => debounce(() => {
    console.log("[WS] Ping timeout");
    socketDisconnect();
  }, WEBSOCKET_PING_TIMEOUT_DELAY), [socketDisconnect]);

  const pingServer = useMemo(() => throttle(() => {
    socketSend("ping");
    timeoutPing();
  }, WEBSOCKET_PING_DELAY, { leading: false, trailing: true }), [socketSend, timeoutPing]);

  const handlePongMessage = useCallback((body: PongMessage) => {
    pingServer();

    if (body.config_hashes) {
      for (const [siteId, newHash] of Object.entries(body.config_hashes)) {
        const oldHash = configHashes.current[siteId];
        configHashes.current[siteId] = newHash;

        if (oldHash && oldHash !== newHash) {
          const listener = configHashListeners.current[siteId];
          if (listener) listener(siteId, newHash);
        }
      }
    }
  }, [pingServer]);

  const subscribeContext = useCallback((siteId: string, context: string, handler: ContextDataHandler) => {
    const ctxId = contextId(siteId, context);

    if (!subscriptions.current[ctxId]) subscriptions.current[ctxId] = [];
    subscriptions.current[ctxId].push(handler);
    socketSend("subscribe", siteId, context);
  }, [socketSend]);

  const unsubscribeContext = useCallback((siteId: string, context: string) => {
    const ctxId = contextId(siteId, context);

    delete subscriptions.current[ctxId];
    socketSend("unsubscribe", siteId, context);
  }, [socketSend]);

  const subscribeConfigHash = useCallback((siteId: string, handler: ConfigHashChangeHandler) => {
    configHashListeners.current[siteId] = handler;
  }, []);

  const unsubscribeConfigHash = useCallback((siteId: string) => {
    delete configHashListeners.current[siteId];
    delete configHashes.current[siteId];
  }, []);

  const pauseContexts = useCallback(() => {
    Object.keys(subscriptions.current).forEach(ctxId => {
      const [siteId, context] = contextIdSplit(ctxId);

      socketSend("unsubscribe", siteId, context);
    });
  }, [socketSend]);

  const resumeContexts = useCallback(() => {
    Object.keys(subscriptions.current).forEach(ctxId => {
      const [siteId, context] = contextIdSplit(ctxId);

      socketSend("subscribe", siteId, context);
    });
  }, [socketSend]);

  const handleMetricsMessage = useCallback((body: MetricsMessage) => {
    const ctxId = contextId(body.site_id, body.context);
    const allSitesCtxId = contextId(ALL_SITES_ID, body.context);

    dataRef.current = {
      ...dataRef.current,
      [body.bucket]: body.payload,
    }
    if (subscriptions.current[ctxId]) subscriptions.current[ctxId].forEach(handler => handler(body));
    if (subscriptions.current[allSitesCtxId]) subscriptions.current[allSitesCtxId].forEach(handler => handler(body));
  }, []);

  const handleMessages = useCallback((body: MetricsMessage | PongMessage) => {
    switch (body.type) {
      case "metrics": handleMetricsMessage(body as MetricsMessage); break;
      case "pong": handlePongMessage(body as PongMessage); break;
    }
  }, [handleMetricsMessage, handlePongMessage]);

  const wsUrl = useCallback((url: string): string => {
    if (url.search(/^ws:/) >= 0) return url;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";

    return `${protocol}//${window.location.host}${url}`;
  }, []);

  const socketConnect = useMemo(() => debounce(() => {
    if (!user?.token) return;
    if (socketRef.current?.readyState === WebSocket.OPEN){
      console.log("[WS] Reusing existing socket.");
      return;
    }

    console.log("[WS] Connecting...");
    let socket = socketRef.current = new WebSocket(
        wsUrl(`${envVars.METRICS_API_URL}?token=${user.token}`)
    );

    socket.onopen = function (_event) {
      console.log("[WS] Connected.");
      setConnected(true);
      resumeContexts();
      pingServer();
    };

    socket.onmessage = function (event) {
      handleMessages(JSON.parse(event.data));
    };

    socket.onclose = function (_event) {
      console.log("[WS] Disconnected.");
      setConnected(false);
      pingServer.cancel();
      timeoutPing.cancel();
      socketConnectRef.current?.();
    };

    socket.onerror = function (error: any) {
      console.log(`[WS] Connection error ${error.message}`);
      socket.close();
    };

  }, WEBSOCKET_CONNECT_DELAY), [resumeContexts, setConnected, handleMessages, pingServer, timeoutPing, user?.token, wsUrl]);

  const socketConnectRef = useRef<ReturnType<typeof debounce> | null>(null);
  socketConnectRef.current = socketConnect;

  useEffect(() => {
    socketConnectRef.current?.cancel();
    socketConnect.cancel();

    return () => {
      socketConnectRef.current?.cancel();
      socketConnect.cancel();
    }
  }, [user?.token, socketConnect]);

  useEffect(() => {
    socketRef.current || socketConnect();

    return () => {
      socketDisconnect();
      isFirstRender.current = true;
    }
  }, [socketConnect, socketDisconnect]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    } else {
      if (documentVisible) {
        resumeContexts();
      } else {
        pauseContexts();
      }
    }
  }, [documentVisible, pauseContexts, resumeContexts]);

  useEffect(() => {
    const closeOnUnload = () => {
      socketRef.current?.close();
    };

    window.addEventListener("beforeunload", closeOnUnload);
    window.addEventListener("pagehide", closeOnUnload);

    return () => {
      window.removeEventListener("beforeunload", closeOnUnload);
      window.removeEventListener("pagehide", closeOnUnload);
    };
  }, []);

  return (
    <WebSocketContext.Provider
      value={{
        subscribeContext,
        unsubscribeContext,
        subscribeConfigHash,
        unsubscribeConfigHash,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): IWebSocket => {
  return useContext(WebSocketContext);
};
